import { inject, Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Transporter } from '../models/transporter.model';
import { Observable, of } from 'rxjs';

export interface PageMetadata {
  description: string;
  author: string;
  keywords: string[];
  type: string;
  viewport: string;
}

const defaultMetadata: PageMetadata = {
  description: "BARY l'application des transporteurs",
  author: 'powered by Bary',
  keywords: [
    'transporteur',
    'BARY',
    'application',
    'chargeur',
    'devis',
    'quotation',
    'interface',
    'digital',
    'similes88',
    'commande',
    'transport',
    'marchandises',
    'colis',
    'palette',
    'um',
    'offre',
    'commerce',
    '60x80',
    '80x120',
    '100x120',
    '120x120',
  ],
  type: 'website',
  viewport: 'width=device-width, initial-scale=1, shrink-to-fit=no',
};

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private metaTagService = inject(Meta);
  private titleService = inject(Title);

  /**
   * Met à jour/normalise toutes les balises SEO essentielles pour le domaine courant
   */
  public updateMetadata(metadata: Partial<PageMetadata>, index: boolean = true, opts?: { title?: string; image?: string; siteName?: string }): void {
    const pageMetadata: PageMetadata = { ...defaultMetadata, ...metadata };

    const host = window.location?.hostname ?? '';
    const internalHost = host.endsWith('baryshop.com');

    // preferredOrigin peut être défini ailleurs (ex: depuis le transporteur.domainName)
    const preferredOrigin = sessionStorage.getItem('preferredOrigin') ?? '';

    const absoluteOrigin = window.location?.origin ?? '';
    const image = opts?.image ?? `${absoluteOrigin}/content/images/logo_bary_512.png`;
    const title = opts?.title ?? 'Bary';
    const siteName = opts?.siteName ?? 'Bary';

    // Choisir l'URL absolue à exposer aux robots/applis (og:url)
    const pathAndQuery = (window.location?.pathname ?? '/') + (window.location?.search ?? '');
    const absoluteUrl = internalHost && preferredOrigin ? `${preferredOrigin}${pathAndQuery}` : window.location?.href ?? '';

    // Titre du document
    try {
      this.titleService.setTitle(title);
    } catch {}

    // Sur un domaine interne, forcer noindex, nofollow
    const robotsContent = internalHost ? 'noindex, nofollow' : index ? 'index, follow' : 'index';

    // Définitions à mettre à jour/créer
    const definitions: MetaDefinition[] = [
      // SEO de base
      { name: 'description', content: pageMetadata.description },
      { name: 'keywords', content: pageMetadata.keywords.join(', ') },
      { name: 'robots', content: robotsContent },
      { name: 'viewport', content: pageMetadata.viewport },
      { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },

      // Open Graph
      { property: 'og:title', content: title },
      { property: 'og:site_name', content: siteName },
      { property: 'og:description', content: pageMetadata.description },
      { property: 'og:type', content: pageMetadata.type },
      { property: 'og:url', content: absoluteUrl },
      { property: 'og:image', content: image },

      // Twitter Card
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: pageMetadata.description },
      { name: 'twitter:image', content: image },
    ];

    // Mettre à jour les balises (updateTag remplace/ajoute si nécessaire)
    definitions.forEach(def => this.metaTagService.updateTag(def));
  }

  public updateMetadataForTransporter(transporter: Transporter): Observable<any> {
    const shop = transporter?.shopName ?? 'Bary';
    const title = `${shop} - Commandez vos transports en ligne`;

    this.updateMetadata(
      {
        description: 'Nouveau ! Passez vos commandes de transport de marchandise routier en toutes simplicité avec ' + shop,
      },
      true,
      { title, siteName: shop }
    );
    return of();
  }
}
